import AppPromoBanner from "@/components/app-promo-banner";
import React, { useState, useEffect } from "react";
import { useCmsList } from "@/hooks/useCmsList";
import { useCmsSection } from "@/hooks/useCmsContent";
import { useCms } from "@/context/CmsContext";
import styles from './index.module.css';

interface FaqItem {
    id?: number;
    section: string;
    question: string;
    answer: string;
}

// Hardcoded fallback FAQs
const FALLBACK_FAQS: FaqItem[] = [
    { section: "General Questions about Kwik Fill", question: "What is the difference between Kwik Fill and Red Apple?", answer: "Kwik Fill® denotes our chain of locations as a whole and also represents our brand of fuel. Red Apple Food Marts® is what we call our convenience stores. All of our locations go by the name Kwik Fill, but not all of them have a Red Apple® convenience store. Some of our locations are full-serve gas stations." },
    { section: "General Questions about Kwik Fill", question: "Is it true that United Refining Company's Kwik Fill Brand gasoline is made in America?", answer: "Yes. United Refining Company operates a 70,000 barrel per-day refinery located in Warren, Pennsylvania and markets its American-made gasoline and diesel through its chain of retail outlets." },
    { section: "General Questions about Kwik Fill", question: "Is it true that Kwik Fill gasoline and diesel are not made from overseas oil?", answer: "Yes. United Refining Company refines with North American crude oil; no overseas crude oil is used to make Kwik Fill branded fuels." },
    { section: "Questions Regarding Kwik Fill Rewards", question: "Where can I use my Kwik Rewards Club Card?", answer: "The Kwik Rewards Card can be used at all company operated Kwik Fill locations as well as participating Kwik Fill Franchise stores. Visit kwikfill.com for our store locator map." },
];

export default function HelpFAQ() {
    const { items: cmsFaqs, loading, add, remove } = useCmsList<FaqItem>('faqs');
    const { fields } = useCmsSection('faq', 'body', {
        title: 'Frequently Asked Questions',
        subtitle: 'If you have a question that is not on this list, please use the contact page and we will respond as quickly as possible.',
    });
    const { isAdmin, isEditMode } = useCms();

    const [activeIndex, setActiveIndex] = useState(-1);
    const [activeSection, setActiveSection] = useState(-1);
    const [showAddForm, setShowAddForm] = useState(false);

    const faqs = cmsFaqs.length > 0 ? cmsFaqs : FALLBACK_FAQS;

    // Group by section
    const sections: { [key: string]: FaqItem[] } = {};
    faqs.forEach(faq => {
        if (!sections[faq.section]) sections[faq.section] = [];
        sections[faq.section].push(faq);
    });

    const toggle = (index: number, sectionIndex: number) => {
        if (index === activeIndex && sectionIndex === activeSection) {
            setActiveIndex(-1);
            setActiveSection(-1);
        } else {
            setActiveIndex(index);
            setActiveSection(sectionIndex);
        }
    };

    return (
        <div className={styles.container}>
            <div>
                <div className={styles.title}>{fields.title}</div>
                <div className={styles.subtitle}>{fields.subtitle}</div>
            </div>

            {isAdmin && isEditMode && (
                <div style={{ marginBottom: 16 }}>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        style={{
                            background: '#3b82f6', color: 'white', border: 'none',
                            padding: '8px 16px', borderRadius: 8, fontSize: 13,
                            fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                        }}
                    >
                        + Add FAQ
                    </button>
                    {showAddForm && (
                        <AddFaqForm
                            existingSections={Object.keys(sections)}
                            onSubmit={async (data) => { await add(data); setShowAddForm(false); }}
                            onCancel={() => setShowAddForm(false)}
                        />
                    )}
                </div>
            )}

            {loading && <div>Loading...</div>}

            {Object.entries(sections).map(([sectionName, items], sectionIndex) => (
                <div key={sectionIndex} className={styles.accordian_container}>
                    <div className={styles.section_header}>{sectionName}</div>
                    {items.map((item, index) => (
                        <div key={item.id || index} className={styles.accordian_wrapper}>
                            <div className={styles.accordian_header_wrapper} onClick={() => toggle(index, sectionIndex)}>
                                <div className={styles.accordian_header}>{item.question}</div>
                                <div className={styles.sign}>
                                    {activeIndex === index && activeSection === sectionIndex ? 'Collapse' : 'Expand'}
                                </div>
                            </div>
                            {activeIndex === index && activeSection === sectionIndex && (
                                <div className={styles.accordian_body} style={{ position: 'relative' }}>
                                    {item.answer}
                                    {isAdmin && isEditMode && item.id && (
                                        <button
                                            onClick={() => remove(item.id!)}
                                            style={{
                                                position: 'absolute', top: 4, right: 4,
                                                background: '#ef4444', color: 'white', border: 'none',
                                                width: 22, height: 22, borderRadius: '50%', fontSize: 13,
                                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            title="Remove FAQ"
                                        >
                                            &times;
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ))}

            <div style={{ marginTop: 120, marginBottom: 60 }}>
                <AppPromoBanner />
            </div>
        </div>
    );
}

interface AddFaqFormProps {
    existingSections: string[];
    onSubmit: (data: Omit<FaqItem, 'id'>) => Promise<void>;
    onCancel: () => void;
}

function AddFaqForm({ existingSections, onSubmit, onCancel }: AddFaqFormProps) {
    const [form, setForm] = useState({ section: existingSections[0] || '', question: '', answer: '' });
    const [customSection, setCustomSection] = useState(false);

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0',
        borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', boxSizing: 'border-box',
    };
    const labelStyle: React.CSSProperties = {
        display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4,
        fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    };

    return (
        <div style={{
            marginTop: 12, padding: 20, background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 12, fontFamily: 'system-ui',
        }}>
            <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Section</label>
                {!customSection ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <select
                            style={{ ...inputStyle, flex: 1 }}
                            value={form.section}
                            onChange={e => setForm({ ...form, section: e.target.value })}
                        >
                            {existingSections.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => { setCustomSection(true); setForm({ ...form, section: '' }); }}
                            style={{ ...inputStyle, width: 'auto', cursor: 'pointer', background: '#f1f5f9' }}>
                            New Section
                        </button>
                    </div>
                ) : (
                    <input style={inputStyle} placeholder="New section name" value={form.section}
                        onChange={e => setForm({ ...form, section: e.target.value })} />
                )}
            </div>
            <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Question</label>
                <input style={inputStyle} value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Answer</label>
                <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.answer}
                    onChange={e => setForm({ ...form, answer: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => onSubmit(form)}
                    disabled={!form.section || !form.question || !form.answer}
                    style={{
                        background: '#22c55e', color: 'white', border: 'none',
                        padding: '8px 18px', borderRadius: 8, fontSize: 13,
                        fontWeight: 600, cursor: 'pointer',
                        opacity: (!form.section || !form.question || !form.answer) ? 0.5 : 1,
                    }}>
                    Add FAQ
                </button>
                <button onClick={onCancel} style={{
                    background: '#6b7280', color: 'white', border: 'none',
                    padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
